import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import {
  Rocket, AlertTriangle, CheckCircle2, Loader2, Copy, Check, ExternalLink,
  ShieldCheck, X, RefreshCw, Wallet,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStickyNetwork, buildExplorerUrl } from "./WalletProvider";
import { createSplToken, paymentService } from "./tokenService";
import { launchFeePaymentService } from "./launchFeePaymentService";
import { activePaymentMode, computeAllocations, LAUNCH_FEE_USD, isLiveMode } from "./launchpadConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_SOL_FOR_MINT = 0.01;

async function persistLaunchpadToken(payload) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/launchpad/tokens`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

const shortAddr = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const usd = (n) => `$${Number(n).toFixed(2)}`;

function CopyChip({ value, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  return (
    <button type="button" className={`sticky-lp-ca-copy ${copied ? "is-copied" : ""}`} onClick={onCopy}>
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copied" : label}
    </button>
  );
}

/* Phase 6 payment states */
const PS = Object.freeze({
  IDLE: "IDLE",
  REVIEW: "REVIEW",
  TEST_PENDING: "TEST_PENDING",
  TEST_CONFIRMED: "TEST_CONFIRMED",
  TOKEN_CREATION_PENDING: "TOKEN_CREATION_PENDING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
});

export function TokenCreator({ form }) {
  const { connection } = useConnection();
  const wallet = useWallet();
  const { network } = useStickyNetwork();
  const navigate = useNavigate();

  // Keep a live Connection reference so mid-flight RPC URL changes are picked up.
  const connectionRef = useRef(connection);
  useEffect(() => { connectionRef.current = connection; }, [connection]);
  const getConnection = useCallback(() => connectionRef.current, []);

  const [paymentState, setPaymentState] = useState(PS.IDLE);
  const [payment, setPayment] = useState(null);           // last test payment record
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const idempotencyRef = useRef(null);
  const [payConfig, setPayConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    paymentService.getPaymentAllocation()
      .then((c) => { if (!cancelled) setPayConfig(c); })
      .catch(() => { /* fallback via computeAllocations below */ });
    return () => { cancelled = true; };
  }, []);

  const feeCfg = useMemo(() => {
    const alloc = computeAllocations(LAUNCH_FEE_USD);
    const cfg = payConfig || {};
    return {
      fee: cfg.feeUsd || LAUNCH_FEE_USD,
      currency: cfg.currency || "USD",
      ecosystem:  cfg.allocations?.ecosystemAllocation  ?? alloc.ecosystem,
      buyBurn:    cfg.allocations?.buyBurnAllocation    ?? alloc.buyBurn,
      operations: cfg.allocations?.operationsAllocation ?? alloc.operations,
      solPreview: cfg.solAmountPreview ?? null,
      buybackStatus: cfg.buyback?.status || "PENDING_STICKY_LAUNCH",
      buybackReady:  Boolean(cfg.buyback?.buybackReady),
      liveEnabled:   Boolean(cfg.liveEnabled),
    };
  }, [payConfig]);

  const readableError = (err) => {
    const msg = String(err?.message || err || "Unknown error");
    if (/reject|denied|cancelled|user rejected/i.test(msg)) return "You cancelled the transaction in your wallet.";
    if (/insufficient|not enough SOL/i.test(msg))          return "Insufficient SOL to pay transaction fees on this network.";
    if (/blockhash|expired/i.test(msg))                    return "Network is slow — the transaction expired. Please try again.";
    if (/failed to fetch|network|timeout/i.test(msg))      return "RPC unreachable. Check the selected network and try again.";
    return msg.replace(/(?:Error:\s*)?(0x[0-9a-fA-F]+)?/g, "").trim() || "Transaction failed.";
  };

  const validate = () => {
    if (!wallet.connected || !wallet.publicKey) return "Please connect your wallet first.";
    if (!form.name?.trim())                     return "Token name is required.";
    if (!form.symbol?.trim())                   return "Token symbol is required.";
    if (!/^[A-Z0-9]{1,12}$/.test((form.symbol || "").toUpperCase())) return "Symbol must be 1–12 uppercase letters/digits.";
    const supply = String(form.supply || "").trim();
    if (!/^\d+$/.test(supply) || supply === "0") return "Supply must be a positive integer.";
    const dec = Number(form.decimals);
    if (!Number.isInteger(dec) || dec < 0 || dec > 9) return "Decimals must be an integer between 0 and 9.";
    return null;
  };

  const onReview = () => {
    setError("");
    setResult(null);
    setPayment(null);
    const invalid = validate();
    if (invalid) { setError(invalid); return; }
    // fresh idempotency key per launch attempt
    idempotencyRef.current = `${wallet.publicKey.toBase58()}-${Date.now()}`;
    setPaymentState(PS.REVIEW);
  };

  const onCancelReview = () => {
    setPaymentState(PS.CANCELLED);
    setStage("");
  };

  const runTokenCreation = useCallback(async (activePayment) => {
    setPaymentState(PS.TOKEN_CREATION_PENDING);
    setStage("Preparing transaction…");
    setError("");
    try {
      const activeConn = getConnection();
      console.log("[STICKY:onCreate] active RPC endpoint:", activeConn?.rpcEndpoint);
      const lamports = await activeConn.getBalance(wallet.publicKey, { commitment: "confirmed" });
      if (lamports / LAMPORTS_PER_SOL < MIN_SOL_FOR_MINT) {
        throw new Error(`You need at least ${MIN_SOL_FOR_MINT} SOL on ${network.label} to cover mint rent + fees.`);
      }

      const out = await createSplToken(
        getConnection,
        wallet,
        { decimals: Number(form.decimals), supply: form.supply },
        setStage,
      );

      // Link the mint to the payment (best-effort — payment is source of truth for retries)
      let linkedPayment = activePayment;
      if (activePayment?.paymentId) {
        const updated = await paymentService.attachMint(activePayment.paymentId, out.mintAddress);
        if (updated) linkedPayment = updated;
      }

      const status = network.id === "LOCALNET"
        ? "LOCALNET_TEST"
        : network.id === "MAINNET" ? "MAINNET_LIVE" : "DEVNET_TEST";
      const record = {
        name: form.name.trim(),
        symbol: (form.symbol || "").toUpperCase().trim(),
        description: (form.description || "").trim(),
        mintAddress: out.mintAddress,
        creatorWallet: wallet.publicKey.toBase58(),
        supply: String(form.supply),
        decimals: Number(form.decimals),
        image: form.logoDataUrl || null,
        website: form.socials?.website || null,
        twitter: form.socials?.x || null,
        telegram: form.socials?.telegram || null,
        network: network.id,
        signature: out.signature,
        status,
        paymentId: linkedPayment?.paymentId || null,
      };
      const persisted = await persistLaunchpadToken(record);
      const metadataSaved = persisted !== null;

      setResult({ ...out, record: persisted || record, network, metadataSaved, payment: linkedPayment });
      setPayment(linkedPayment);
      setPaymentState(PS.COMPLETED);
      setStage("Token created successfully.");
    } catch (err) {
      setError(readableError(err));
      setStage("");
      setPaymentState(PS.FAILED);
    }
  }, [getConnection, wallet, form, network]);

  const onConfirmTestPayment = useCallback(async () => {
    if (!wallet.connected || !wallet.publicKey) {
      setError("Please connect your wallet first.");
      return;
    }
    setError("");
    setPaymentState(PS.TEST_PENDING);
    setStage("Confirming test payment…");
    let created;
    try {
      created = await paymentService.createTestPayment({
        wallet: wallet.publicKey.toBase58(),
        mode: activePaymentMode(network.id),
        idempotencyKey: idempotencyRef.current,
      });
    } catch (e) {
      setError("Payment confirmation failed. " + (e?.message || ""));
      setPaymentState(PS.FAILED);
      setStage("");
      return;
    }
    if (created?.status !== "TEST_CONFIRMED") {
      setError("Payment confirmation failed.");
      setPaymentState(PS.FAILED);
      setStage("");
      return;
    }
    setPayment(created);
    setPaymentState(PS.TEST_CONFIRMED);
    // Immediately continue to token creation
    await runTokenCreation(created);
  }, [wallet, network, runTokenCreation]);

  /**
   * Phase 10 — LIVE (Mainnet) payment flow. Only reachable when the wallet
   * is on MAINNET and the backend reports liveEnabled=true. Failure to meet
   * either condition falls back to the TEST path silently at the call site.
   */
  const onConfirmLivePayment = useCallback(async () => {
    if (!wallet.connected || !wallet.publicKey) {
      setError("Please connect your wallet first.");
      return;
    }
    setError("");
    setPaymentState(PS.TEST_PENDING); // reuse "pending" state — UI only shows a spinner
    setStage("Preparing live payment…");
    let confirmed;
    try {
      confirmed = await launchFeePaymentService.runLivePayment({
        connection: getConnection(),
        wallet,
        networkId: network.id,
        idempotencyKey: idempotencyRef.current,
        onStage: setStage,
      });
    } catch (e) {
      setError(readableError(e));
      setPaymentState(PS.FAILED);
      setStage("");
      return;
    }
    if (confirmed?.status !== "CONFIRMED") {
      setError("Live payment could not be confirmed. Please retry.");
      setPaymentState(PS.FAILED);
      setStage("");
      return;
    }
    setPayment(confirmed);
    setPaymentState(PS.TEST_CONFIRMED); // reuse success gate — payment is confirmed
    await runTokenCreation(confirmed);
  }, [wallet, network, getConnection, runTokenCreation]);

  const onRetryTokenCreation = useCallback(() => {
    if (!payment) { onReview(); return; }
    // Do NOT re-create the payment — reuse the existing TEST_CONFIRMED record.
    runTokenCreation(payment);
  }, [payment, runTokenCreation]);

  /* -------------------------- Rendering -------------------------- */
  const busy = paymentState === PS.TEST_PENDING || paymentState === PS.TOKEN_CREATION_PENDING;
  const showReview = paymentState === PS.REVIEW || paymentState === PS.TEST_PENDING;
  const showFailedAfterPayment = paymentState === PS.FAILED && (payment?.status === "TEST_CONFIRMED" || payment?.status === "CONFIRMED") && !result;

  // Phase 10 — decide TEST vs LIVE for the confirm button.
  const activeMode = activePaymentMode(network.id);
  const liveActive = isLiveMode(activeMode) && Boolean(feeCfg.liveEnabled);
  const onConfirmClick = liveActive ? onConfirmLivePayment : onConfirmTestPayment;
  const confirmLabel = liveActive ? "Pay $99 & Launch" : "Confirm Test Payment & Launch";
  const modeBanner = liveActive
    ? { text: "MAINNET LIVE · Real SOL will be transferred", cls: "is-live" }
    : { text: `${network.id === "LOCALNET" ? "LOCALNET" : "DEVNET"} TEST MODE · No real funds will be charged`, cls: "" };

  return (
    <div className="sticky-lp-creator" data-testid="lp-token-creator">
      {/* Initial CTA */}
      {!result && !showReview && !showFailedAfterPayment && (
        <>
          <div className="sticky-lp-testmode" role="status" data-testid="lp-testmode-banner">
            <ShieldCheck size={13} /> {network.id === "LOCALNET" ? "LOCALNET TEST MODE" : "DEVNET TEST MODE"} · No real funds will be charged
          </div>
          <button
            type="button"
            className="sticky-lp-btn is-primary sticky-lp-submit"
            onClick={onReview}
            disabled={!wallet.connected}
            data-testid="lp-create-token"
          >
            <Rocket size={14} /> Review Launch
            <span className="sticky-lp-soon-inline">{network.shortLabel}</span>
          </button>

          {!wallet.connected && (
            <p className="sticky-lp-note sticky-lp-note-center">
              Connect a wallet on <b>{network.label}</b> to enable the review.
            </p>
          )}

          {paymentState === PS.CANCELLED && (
            <div className="sticky-lp-error sticky-lp-error-info" role="status" data-testid="lp-launch-cancelled">
              <AlertTriangle size={14} />
              <span>Launch cancelled. No token has been created.</span>
            </div>
          )}

          {error && (
            <div className="sticky-lp-error" role="alert" data-testid="lp-create-error">
              <AlertTriangle size={14} />
              <span>{error}</span>
            </div>
          )}
        </>
      )}

      {/* Review + payment panel */}
      {showReview && (
        <div className="sticky-lp-pay" data-testid="lp-pay-review">
          <div className="sticky-lp-pay-head">
            <div>
              <div className="sticky-lp-pay-title">Launch Your Token</div>
              <div className="sticky-lp-pay-sub">Sticky Launchpad · {network.label}</div>
            </div>
            <button type="button" className="sticky-lp-pay-close" onClick={onCancelReview} aria-label="Cancel" data-testid="lp-pay-close" disabled={busy}>
              <X size={14} />
            </button>
          </div>

          <div className={`sticky-lp-testmode is-lg ${modeBanner.cls}`} role="status" data-testid="lp-pay-testmode">
            <ShieldCheck size={13} /> {modeBanner.text}
          </div>

          <div className="sticky-lp-pay-fee">
            <span>Launch Fee</span>
            <b data-testid="lp-pay-fee">${feeCfg.fee}</b>
            <em>{feeCfg.currency}</em>
            {feeCfg.solPreview && (
              <span className="sticky-lp-pay-sol-preview" data-testid="lp-pay-sol-preview">
                ≈ {feeCfg.solPreview} SOL {liveActive ? "(live)" : "preview"}
              </span>
            )}
          </div>

          <div className="sticky-lp-pay-splits">
            <div className="sticky-lp-pay-split" data-testid="lp-split-ecosystem">
              <div className="sticky-lp-pay-split-pct">40%</div>
              <div>
                <div className="sticky-lp-pay-split-name">STICKY Ecosystem</div>
                <div className="sticky-lp-pay-split-amt">{usd(feeCfg.ecosystem)}</div>
              </div>
            </div>
            <div className="sticky-lp-pay-split" data-testid="lp-split-buyburn">
              <div className="sticky-lp-pay-split-pct">40%</div>
              <div>
                <div className="sticky-lp-pay-split-name">STICKY Buy &amp; Burn</div>
                <div className="sticky-lp-pay-split-amt">{usd(feeCfg.buyBurn)}</div>
                <div className="sticky-lp-pay-split-note" data-testid="lp-buyburn-status">
                  {feeCfg.buybackReady
                    ? "STICKY buyback armed"
                    : "STICKY has not launched yet · Buy & Burn pending (PENDING_STICKY_LAUNCH)"}
                </div>
              </div>
            </div>
            <div className="sticky-lp-pay-split" data-testid="lp-split-operations">
              <div className="sticky-lp-pay-split-pct">20%</div>
              <div>
                <div className="sticky-lp-pay-split-name">Launchpad Operations</div>
                <div className="sticky-lp-pay-split-amt">{usd(feeCfg.operations)}</div>
              </div>
            </div>
          </div>

          <div className="sticky-lp-pay-summary">
            <div><Wallet size={11} /> Wallet</div>
            <code>{shortAddr(wallet.publicKey?.toBase58())}</code>
          </div>

          {busy && (
            <div className="sticky-lp-stage" data-testid="lp-pay-stage">
              <Loader2 size={14} className="is-spinning" />
              <span>{stage || "Confirming test payment…"}</span>
            </div>
          )}

          <div className="sticky-lp-pay-actions">
            <button
              type="button"
              className="sticky-lp-btn is-ghost"
              onClick={onCancelReview}
              disabled={busy}
              data-testid="lp-pay-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="sticky-lp-btn is-primary sticky-lp-pay-confirm"
              onClick={onConfirmClick}
              disabled={busy}
              data-testid="lp-pay-confirm"
            >
              {busy ? <Loader2 size={14} className="is-spinning" /> : <ShieldCheck size={14} />}
              {busy ? (stage || "Confirming…") : confirmLabel}
            </button>
          </div>

          <p className="sticky-lp-note sticky-lp-note-center">
            {liveActive
              ? "You will sign a single transaction that splits your $99 fee 40/40/20 across the three public treasuries."
              : "This is a test payment. Your Localnet SOL balance is not affected."}
          </p>
        </div>
      )}

      {/* Payment confirmed but token creation failed — allow retry without new payment */}
      {showFailedAfterPayment && (
        <div className="sticky-lp-pay is-recovery" data-testid="lp-pay-recovery">
          <div className="sticky-lp-pay-title">Payment confirmed, but token creation failed.</div>
          <p className="sticky-lp-pay-sub">
            Your test payment is preserved (id <code>{shortAddr(payment.paymentId)}</code>).
            You will not be charged again. Retry token creation whenever you're ready.
          </p>
          {error && (
            <div className="sticky-lp-error" role="alert">
              <AlertTriangle size={14} /> <span>{error}</span>
            </div>
          )}
          <div className="sticky-lp-pay-actions">
            <button type="button" className="sticky-lp-btn is-ghost" onClick={() => { setPaymentState(PS.IDLE); setPayment(null); setError(""); }} data-testid="lp-pay-abandon">
              Discard
            </button>
            <button type="button" className="sticky-lp-btn is-primary" onClick={onRetryTokenCreation} data-testid="lp-pay-retry-token">
              <RefreshCw size={13} /> Retry Token Creation
            </button>
          </div>
        </div>
      )}

      {/* Success card */}
      {result && (
        <div className="sticky-lp-success" data-testid="lp-create-success">
          <div className="sticky-lp-success-head">
            <CheckCircle2 size={20} />
            <div>
              <div className="sticky-lp-success-title">Token Created Successfully</div>
              <div className="sticky-lp-success-net">{result.network.shortLabel}</div>
            </div>
          </div>

          {result.payment?.paymentId && (
            <div className="sticky-lp-pay-receipt" data-testid="lp-pay-receipt">
              <span>Launch Fee</span>
              <b>{usd(result.payment.amount || LAUNCH_FEE_USD)} · {result.payment.mode.replace("_", " ")}</b>
              <em>Status: {result.payment.status.replace("_", " ")}</em>
            </div>
          )}

          {result.metadataSaved === false && (
            <div className="sticky-lp-error" role="alert" data-testid="lp-metadata-warning" style={{ marginBottom: 12 }}>
              <AlertTriangle size={14} />
              <span>The SPL token was created on-chain, but the metadata could not be saved to the Launchpad registry. Copy the mint address and signature below — you can retry the registry save later.</span>
            </div>
          )}
          {result.metadataSaved !== false && <AutoRedirect to={`/launchpad/token/${result.mintAddress}`} />}

          <div className="sticky-lp-success-grid">
            <div><span>Name</span><b>{form.name}</b></div>
            <div><span>Symbol</span><b>${(form.symbol || "").toUpperCase()}</b></div>
            <div><span>Total Supply</span><b>{Number(form.supply).toLocaleString("en-US")}</b></div>
            <div><span>Decimals</span><b>{form.decimals}</b></div>
          </div>
          <div className="sticky-lp-success-row">
            <span className="sticky-lp-success-label">Mint Address</span>
            <code>{shortAddr(result.mintAddress)}</code>
            <CopyChip value={result.mintAddress} />
            <a href={buildExplorerUrl("address", result.mintAddress, result.network)} target="_blank" rel="noopener noreferrer" className="sticky-lp-success-ext">
              <ExternalLink size={11} /> Explorer
            </a>
          </div>
          <div className="sticky-lp-success-row">
            <span className="sticky-lp-success-label">Creator</span>
            <code>{shortAddr(wallet.publicKey?.toBase58())}</code>
            <CopyChip value={wallet.publicKey?.toBase58() || ""} />
          </div>
          <div className="sticky-lp-success-row">
            <span className="sticky-lp-success-label">Signature</span>
            <code>{shortAddr(result.signature)}</code>
            <CopyChip value={result.signature} />
            <a href={buildExplorerUrl("tx", result.signature, result.network)} target="_blank" rel="noopener noreferrer" className="sticky-lp-success-ext">
              <ExternalLink size={11} /> Tx
            </a>
          </div>
          <button
            type="button"
            className="sticky-lp-btn is-primary sticky-lp-success-cta"
            onClick={() => navigate(`/launchpad/token/${result.mintAddress}`)}
            data-testid="lp-view-token-page"
          >
            <Rocket size={14} /> View Token Page {result.metadataSaved !== false && <span className="sticky-lp-soon-inline">Auto-redirect · 2s</span>}
          </button>
        </div>
      )}
    </div>
  );
}

function AutoRedirect({ to }) {
  const navigate = useNavigate();
  useEffect(() => {
    const id = setTimeout(() => navigate(to), 2000);
    return () => clearTimeout(id);
  }, [to, navigate]);
  return null;
}
